import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Receipt,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import { useAuthStore } from "@/store/auth";
import { externalCostsApi } from "../api/externalCosts";
import { useContractorPreview } from "../contractorPreview";
import { useOrderAccess } from "../orderAccess";
import {
  EXTERNAL_COST_STATUS_LABELS,
  EXTERNAL_COST_TYPE_LABELS,
  type ExternalCostStatus,
  type ExternalCostType,
} from "../types";

const STATUS_COLOR: Record<
  ExternalCostStatus,
  "yellow" | "green" | "red" | "blue" | "gray"
> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
  incurred: "blue",
  settled: "gray",
};

interface Props {
  orderId: string;
  allowExternalCosts?: boolean;
}

export function ExternalCostsSection({ orderId, allowExternalCosts = true }: Props) {
  const qc = useQueryClient();
  const { user } = useAuthStore();
  // W „widoku podwykonawcy" (podgląd GK) sekcja renderuje wariant podwykonawcy,
  // a zgłaszanie kosztów jest wyłączone.
  const { isPreview } = useContractorPreview();
  // Grant „Zarządzanie" rozpatruje koszty jak koordynator; „Podgląd" tylko czyta.
  const { readOnly: accessReadOnly, canManage: accessCanManage } = useOrderAccess();
  const isCoordinator =
    !isPreview && !accessReadOnly && (user?.role === "coordinator" || user?.role === "director" || accessCanManage);
  const isContractor = user?.role === "contractor" || isPreview;

  const { data: costs = [], isLoading } = useQuery({
    queryKey: ["external-costs", orderId],
    queryFn: () => externalCostsApi.list(orderId),
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    cost_type: "notarial" as ExternalCostType,
    description: "",
    amount_estimated: "",
    justification: "",
  });
  const [formError, setFormError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Approve modal state
  const [approveId, setApproveId] = useState<string | null>(null);
  const [approvedAmount, setApprovedAmount] = useState("");
  const [approvalNote, setApprovalNote] = useState("");

  // Reject modal state
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectionComment, setRejectionComment] = useState("");

  // Submit invoice state
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [amountActual, setAmountActual] = useState("");
  const [settlementMethod, setSettlementMethod] = useState("");

  // Settle state
  const [settleId, setSettleId] = useState<string | null>(null);
  const [settlementNote, setSettlementNote] = useState("");

  const createMutation = useMutation({
    mutationFn: () => externalCostsApi.create({ order: orderId, ...form }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["external-costs", orderId] });
      setShowForm(false);
      setForm({
        cost_type: "notarial",
        description: "",
        amount_estimated: "",
        justification: "",
      });
      setFormError("");
    },
    onError: () => setFormError("Nie udało się złożyć wniosku."),
  });

  const approveMutation = useMutation({
    mutationFn: ({
      id,
      amount,
      note,
    }: {
      id: string;
      amount: string;
      note: string;
    }) => externalCostsApi.approve(id, amount, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["external-costs", orderId] });
      setApproveId(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      externalCostsApi.reject(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["external-costs", orderId] });
      setRejectId(null);
    },
  });

  const invoiceMutation = useMutation({
    mutationFn: ({
      id,
      amount,
      method,
    }: {
      id: string;
      amount: string;
      method: string;
    }) => externalCostsApi.submitInvoice(id, amount, method),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["external-costs", orderId] });
      setInvoiceId(null);
    },
  });

  const settleMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      externalCostsApi.settle(id, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["external-costs", orderId] });
      setSettleId(null);
    },
  });

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (isLoading) return null;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Receipt size={18} />
          Koszty zewnętrzne
        </h2>
        {isContractor && allowExternalCosts && (
          <Button
            size="sm"
            variant="secondary"
            disabled={isPreview}
            onClick={() => setShowForm((p) => !p)}
          >
            {showForm ? "Anuluj" : "+ Nowy wniosek"}
          </Button>
        )}
        {isContractor && !allowExternalCosts && (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            Wyłączone przez umowę ramową
          </span>
        )}
      </div>

      {/* Create form (contractor only) */}
      {showForm && isContractor && allowExternalCosts && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Typ kosztu
              </label>
              <select
                className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                value={form.cost_type}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    cost_type: e.target.value as ExternalCostType,
                  }))
                }
              >
                {(
                  Object.entries(EXTERNAL_COST_TYPE_LABELS) as [
                    ExternalCostType,
                    string,
                  ][]
                ).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Kwota netto (PLN)
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="np. 500.00"
                className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                value={form.amount_estimated}
                onChange={(e) =>
                  setForm((p) => ({ ...p, amount_estimated: e.target.value }))
                }
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Opis kosztu
              </label>
              <input
                type="text"
                className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                placeholder="Krótki opis..."
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uzasadnienie <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={2}
                className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                placeholder="Dlaczego koszt jest niezbędny..."
                value={form.justification}
                onChange={(e) =>
                  setForm((p) => ({ ...p, justification: e.target.value }))
                }
              />
            </div>
          </div>
          {formError && (
            <p className="text-xs text-red-600 mb-2">{formError}</p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={createMutation.isPending}
              onClick={() => {
                if (!form.justification.trim()) {
                  setFormError("Uzasadnienie jest wymagane.");
                  return;
                }
                if (
                  !form.amount_estimated ||
                  parseFloat(form.amount_estimated) <= 0
                ) {
                  setFormError("Kwota musi być większa od zera.");
                  return;
                }
                createMutation.mutate();
              }}
            >
              Złóż wniosek
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {costs.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak wniosków o koszty zewnętrzne.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {costs.map((cost) => {
            const isExpanded = expanded.has(cost.id);
            return (
              <div
                key={cost.id}
                className="rounded-xl bg-white dark:bg-gray-800 overflow-hidden"
              >
                <div
                  className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer"
                  onClick={() => toggle(cost.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <Badge color={STATUS_COLOR[cost.status]}>
                        {EXTERNAL_COST_STATUS_LABELS[cost.status]}
                      </Badge>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {cost.cost_type_display}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatAmount(cost.amount_estimated)} PLN szac.
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {cost.requested_by_name} ·{" "}
                      {new Date(cost.created_at).toLocaleDateString("pl")}
                    </p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp size={15} />
                  ) : (
                    <ChevronDown size={15} />
                  )}
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 space-y-2">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {cost.description}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <strong>Uzasadnienie:</strong> {cost.justification}
                    </p>

                    {cost.approved_amount && (
                      <p className="text-xs text-green-700 dark:text-green-400">
                        Zatwierdzona kwota:{" "}
                        <strong>
                          {formatAmount(cost.approved_amount)} PLN
                        </strong>
                        {cost.approved_by_name && ` · ${cost.approved_by_name}`}
                        {cost.approval_note && ` · ${cost.approval_note}`}
                      </p>
                    )}
                    {cost.rejection_comment && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Powód odrzucenia: {cost.rejection_comment}
                      </p>
                    )}
                    {cost.amount_actual && (
                      <p className="text-xs text-[var(--color-primary)] dark:text-blue-400">
                        Kwota rzeczywista:{" "}
                        <strong>{formatAmount(cost.amount_actual)} PLN</strong>
                        {cost.settlement_method &&
                          ` (${cost.settlement_method})`}
                      </p>
                    )}
                    {cost.settlement_note && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Nota rozliczeniowa: {cost.settlement_note}
                      </p>
                    )}

                    {/* Coordinator: approve / reject pending */}
                    {isCoordinator && cost.status === "pending" && (
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={() => {
                            setApproveId(cost.id);
                            setApprovedAmount(cost.amount_estimated);
                            setApprovalNote("");
                          }}
                        >
                          <CheckCircle2 size={13} /> Zatwierdź
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setRejectId(cost.id);
                            setRejectionComment("");
                          }}
                        >
                          <XCircle size={13} /> Odrzuć
                        </Button>
                      </div>
                    )}

                    {/* Coordinator: settle incurred */}
                    {isCoordinator && cost.status === "incurred" && (
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={() => {
                            setSettleId(cost.id);
                            setSettlementNote("");
                          }}
                        >
                          Rozlicz
                        </Button>
                      </div>
                    )}

                    {/* Contractor: submit invoice after approval */}
                    {isContractor && cost.status === "approved" && (
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setInvoiceId(cost.id);
                            setAmountActual("");
                            setSettlementMethod("");
                          }}
                        >
                          Prześlij dokument kosztu
                        </Button>
                      </div>
                    )}

                    {/* Inline forms */}
                    {approveId === cost.id && (
                      <div
                        className="mt-2 space-y-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-0.5">
                              Zatwierdzona kwota (PLN)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="block w-full rounded border border-gray-300 text-sm px-2 py-1"
                              value={approvedAmount}
                              onChange={(e) =>
                                setApprovedAmount(e.target.value)
                              }
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-0.5">
                              Uwaga (opcjonalnie)
                            </label>
                            <input
                              type="text"
                              className="block w-full rounded border border-gray-300 text-sm px-2 py-1"
                              value={approvalNote}
                              onChange={(e) => setApprovalNote(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            loading={approveMutation.isPending}
                            onClick={() =>
                              approveMutation.mutate({
                                id: cost.id,
                                amount: approvedAmount,
                                note: approvalNote,
                              })
                            }
                          >
                            Potwierdź zatwierdzenie
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setApproveId(null)}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </div>
                    )}

                    {rejectId === cost.id && (
                      <div
                        className="mt-2 space-y-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <textarea
                          rows={2}
                          className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-2 py-1"
                          placeholder="Powód odrzucenia..."
                          value={rejectionComment}
                          onChange={(e) => setRejectionComment(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={rejectMutation.isPending}
                            onClick={() =>
                              rejectMutation.mutate({
                                id: cost.id,
                                comment: rejectionComment,
                              })
                            }
                          >
                            Potwierdź odrzucenie
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setRejectId(null)}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </div>
                    )}

                    {invoiceId === cost.id && (
                      <div
                        className="mt-2 space-y-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-0.5">
                              Kwota rzeczywista (PLN){" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              max={cost.approved_amount ?? undefined}
                              className="block w-full rounded border border-gray-300 text-sm px-2 py-1"
                              value={amountActual}
                              onChange={(e) => setAmountActual(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-0.5">
                              Forma rozliczenia
                            </label>
                            <input
                              type="text"
                              placeholder="np. refaktura, nota"
                              className="block w-full rounded border border-gray-300 text-sm px-2 py-1"
                              value={settlementMethod}
                              onChange={(e) =>
                                setSettlementMethod(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            loading={invoiceMutation.isPending}
                            onClick={() =>
                              invoiceMutation.mutate({
                                id: cost.id,
                                amount: amountActual,
                                method: settlementMethod,
                              })
                            }
                          >
                            Prześlij
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setInvoiceId(null)}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </div>
                    )}

                    {settleId === cost.id && (
                      <div
                        className="mt-2 space-y-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-2 py-1"
                          placeholder="Nota rozliczeniowa (opcjonalnie)"
                          value={settlementNote}
                          onChange={(e) => setSettlementNote(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            loading={settleMutation.isPending}
                            onClick={() =>
                              settleMutation.mutate({
                                id: cost.id,
                                note: settlementNote,
                              })
                            }
                          >
                            Potwierdź rozliczenie
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setSettleId(null)}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
